import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useRealmApp } from "../RealmApp";

export default function useRoomMutations() {
  const app = useRealmApp();
  return {
    addRoom: useAddRoom(app.currentUser.id),
    updateRoom: useUpdateRoom(app.currentUser.id),
    deleteRoom: useDeleteRoom(app.currentUser.id),
  };
}

const AddRoomMutation = gql`
  mutation AddRoom($room: RoomInsertInput!) {
    addedRoom: insertOneRoom(data: $room) {
      _id
      storeId
      name
      width
      height
      virtual
      priceIncrement
      priceLists
      updateDatetime
      creationDatetime
      tables {
        _id
        name
        type
        color
        capacity
        posX
        posY
        width
        height
      }
      decorations {
        _id
        name
        type
        color
        posX
        posY
        width
        height
      }
    }
  }
`;

const UpdateRoomMutation = gql`
  mutation UpdateRoom(
    $roomId: ObjectId!
    $ownerId: String!
    $updates: RoomUpdateInput!
  ) {
    updatedRoom: updateOneRoom(
      query: { _id: $roomId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      storeId
      name
      width
      height
      virtual
      priceIncrement
      priceLists
      updateDatetime
      creationDatetime
      tables {
        _id
        owner_id
        name
        type
        color
        capacity
        posX
        posY
        width
        height
      }
      decorations {
        _id
        name
        type
        color
        posX
        posY
        width
        height
      }
    }
  }
`;

const DeleteRoomMutation = gql`
  mutation DeleteRoom($roomId: ObjectId!, $ownerId: String!) {
    deletedRoom: deleteOneRoom(query: { _id: $roomId, owner_id: $ownerId }) {
      _id
    }
  }
`;

const RoomFieldsFragment = gql`
  fragment RoomFields on Room {
    _id
    storeId
    name
    width
    height
    virtual
    priceIncrement
    priceLists
    updateDatetime
    creationDatetime
    tables {
      _id
      name
      type
      color
      capacity
      posX
      posY
      width
      height
    }
    decorations {
      _id
      name
      type
      color
      posX
      posY
      width
      height
    }
  }
`;

function useAddRoom(ownerId) {
  const [addRoomMutation] = useMutation(AddRoomMutation, {
    // Manually save added Rooms into the Apollo cache so that Room queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedRoom } }) => {
      cache.modify({
        fields: {
          rooms: (existingRooms = []) => [
            ...existingRooms,
            cache.writeFragment({
              data: addedRoom,
              fragment: RoomFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (room) => {
    const {
      data: { addedRoom },
    } = await addRoomMutation({
      variables: {
        room: {
          owner_id: ownerId,
          ...room,
        },
      },
    });
    return addedRoom;
  };
}

function useUpdateRoom(ownerId) {
  const [updateRoomMutation] = useMutation(UpdateRoomMutation);
  //
  return async (room, updates) => {
    const {
      data: { updatedRoom },
    } = await updateRoomMutation({
      variables: { roomId: room._id, ownerId, updates },
    });
    return updatedRoom;
  };
}

function useDeleteRoom(ownerId) {
  const [deleteRoomMutation] = useMutation(DeleteRoomMutation, {
    update: (cache, { data: { deletedRoom } }) => {
      cache.modify({
        fields: {
          rooms: (existingRooms = [], { readField }) =>
            existingRooms.filter(
              (ref) => deletedRoom._id !== readField("_id", ref)
            ),
        },
      });
    },
  });
  return async (room) => {
    const {
      data: { deletedRoom },
    } = await deleteRoomMutation({
      variables: { roomId: room._id, ownerId },
    });
    return deletedRoom;
  };
}
